import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { BodyText, Icon, theme } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { locationResultsListsV2Messages as messages } from './LocationResultsListsV2ViewMap.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2ViewMap = (props: LocationResultsListsV2ViewMapProps) => {
  const { onPress } = props;

  const { track } = useTelemetry();

  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.();

    track('location.mapview.tapped');
  }, [onPress, track]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    isLocationSelectionUpdateEnabled ? styles.containerUpdate : styles.containerLegacy,
  ];

  return (
    <TouchableOpacity
      activeOpacity={ACTIVE_OPACITY}
      accessibilityRole="button"
      style={containerStyles}
      onPress={handleOnPress}
    >
      {isLocationSelectionUpdateEnabled ? (
        <Icon
          name="IconPickupStrokeLg"
          color={theme.colors.DARK_KALE}
          width={VIEW_MAP_ICON_SIZE}
          height={VIEW_MAP_ICON_SIZE}
        />
      ) : (
        <Icon name="IconMapPin" />
      )}

      <BodyText
        sizeMatch={['14']}
        style={isLocationSelectionUpdateEnabled ? styles.label : undefined}
      >
        <FormattedMessage {...messages.viewMapButton} />
      </BodyText>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;
const VIEW_MAP_ICON_SIZE = 20;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['1'],
    borderRadius: theme.radius.xlarge,
    minHeight: 40,
    paddingVertical: theme.spacing[1],
    paddingHorizontal: theme.spacing[4],
  },
  containerLegacy: {
    backgroundColor: theme.colors.LINEN,
  },
  containerUpdate: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  label: {
    color: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsV2ViewMapProps = {
  onPress?: () => void;
};
